import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';

import combinedActions from '~redux/combinedActions';
import { useAppDispatch, useAppSelector } from '~redux/hooks';
import {
    FundTypeEnum,
    GoldTypeEnum,
    InventoryActionEnum,
    ModuleEnum,
    PermissionsEnum,
    SilverTypeEnum,
    TransactionStatusEnum,
} from '~utils/enums';

import { TUpdateInventoryTransactionStatusReduxParams } from '~types/feat/approval';

import Image from '~components/atoms/Image';
import Table from '~components/molecules/Table';
import ActionButton from '~components/atoms/ActionButton';
import ViewSerialNoModalModal from '~components/molecules/ViewSerialNoModal';

import * as Styles from './_goldInventoryApprovalTable.module.scss';
import Text from '~components/atoms/Text';

const GoldInventoryApprovalTable = () => {
    const dispatch = useAppDispatch();

    const inventoryTransactionListing = useAppSelector(state => state.approval.pendingInventoryTransactionListing.data);
    const getPendingInventoryTransactionListingIsLoading = useAppSelector(state => state.approval.loadings.getPendingInventoryTransactionListing);
    const access = useAppSelector(state => state.auth.userDetails.access);

    const [serialNoList, setSerialNoList] = useState<string[]>([]);
    const [isSerialNoModalOpen, setIsSerialNoModalOpen] = useState(false);

    const hasEditAccess = access.find(item => item.permission === PermissionsEnum.Edit)?.modules.find(module => module === ModuleEnum.Approval);

    const setTempUpdateInventoryTransactionState = (data: TUpdateInventoryTransactionStatusReduxParams) => dispatch(combinedActions.approvalSetTempUpdateInventoryTransactionState({ inventoryLogId: data.inventoryLogId, status: data.status }));
    const setConfirmUpdateStatusModalIsOpen = (open: boolean) => dispatch(combinedActions.approvalSetConfirmUpdateInventoryTransactionStatusModalIsOpen(open));

    const handleApproveClick = (data: TUpdateInventoryTransactionStatusReduxParams) => {
        setConfirmUpdateStatusModalIsOpen(true);
        setTempUpdateInventoryTransactionState(data);
    };

    const handleRejectClick = (data: TUpdateInventoryTransactionStatusReduxParams) => {
        setConfirmUpdateStatusModalIsOpen(true);
        setTempUpdateInventoryTransactionState(data);
    };

    const tableHeader = [
        'PRODUCT NAME',
        'TRANSACTION TYPE',
        'IMAGE',
        'AMOUNT',
        'PHYSICAL GOLD WEIGHT',
        'PAPER GOLD WEIGHT (G)',
        'SERIAL NUMBER',
        'BRANCH CODE',
        'BRANCH NAME',
        'TOTAL AMOUNT (RM)',
        'FUND TYPE',
        'GOLD TYPE',
        'SILVER TYPE',
        'TRANSACTION DATE',
        'ACTION',
    ];

    const tableBody = inventoryTransactionListing.map(transaction => {
        const {
            productName,
            image,
            physicalGoldWeight,
            paperGoldWeight,
            code,
            branchCode,
            branchName,
            fundType,
            goldType,
            silverType,
            action,
            totalPrice,
            createdAt,
            inventoryLogId,
            amount,
        } = transaction;

        const renderAction = () => {
            if (!action) return '-';

            if (action == InventoryActionEnum.TransferFrom) return 'Transfer From';
            if (action == InventoryActionEnum.TransferTo) return 'Transfer To';
            if (action == InventoryActionEnum.BankSell) return 'Bank Sell';
            if (action == InventoryActionEnum.BankBuy) return 'Bank Buy';
            if (action == InventoryActionEnum.NewProduct) return 'New Product';
            if (action == InventoryActionEnum.RejectTransaction) return 'Reject Transaction';
            if (action == InventoryActionEnum.CancelTransaction) return 'Cancel Transaction';

            return InventoryActionEnum[action];
        };

        const massagedCode =
            code?.map(serial => serial.serialNo).filter(Boolean) || [];

        const handleViewSerialNo = () => {
            setIsSerialNoModalOpen(true);
            setSerialNoList(massagedCode);
        };

        const renderSerialNo = () => {
            return (
                <ActionButton
                    key={inventoryLogId}
                    label='View'
                    onClick={() => handleViewSerialNo()}
                />
            );
        };

        const isGoldRow =
            goldType === GoldTypeEnum.PhysicalGold ||
            goldType === GoldTypeEnum.PaperGold;

        return {
            className: isGoldRow
                ? Styles['gold-row']
                : '',
            cells: [
                productName,
                (
                    <Text
                        key={inventoryLogId}
                        label={renderAction() || ''}
                        className={Styles['transaction-action-label']}
                        isBold
                    />
                ),
                <Image
                    key={inventoryLogId}
                    src={image}
                    width={40}
                    height={40}
                />,
                goldType === GoldTypeEnum.PhysicalGold ? amount : 'N/A',
                `${physicalGoldWeight ? physicalGoldWeight : 0} g`,
                `${paperGoldWeight ? paperGoldWeight : 0} g`,
                renderSerialNo(),
                branchCode,
                branchName,
                totalPrice ? (Math.round(totalPrice * 100) / 100).toFixed(2) : 'N/A',
                FundTypeEnum[fundType] || '-',
                goldType
                    ? GoldTypeEnum[goldType]
                        .split(/(?=[A-Z])/)
                        .join(' ')
                    : '-',
                silverType
                    ? SilverTypeEnum[silverType]
                        .split(/(?=[A-Z])/)
                        .join(' ')
                    : '-',
                dayjs(createdAt).format('DD/MM/YYYY hh:mm A'),
                (
                    <div
                        key={inventoryLogId}
                        className={Styles['action-button-container']}
                    >
                        {hasEditAccess
                            && (
                                <ActionButton
                                    label='Approve'
                                    onClick={() => handleApproveClick({ inventoryLogId, status: TransactionStatusEnum.Approved })}
                                    className={`${Styles['action-button']} ${Styles.approve}`}
                                />
                            )
                        }

                        {hasEditAccess
                            && (
                                <ActionButton
                                    label='Reject'
                                    onClick={() => handleRejectClick({ inventoryLogId, status: TransactionStatusEnum.Rejected })}
                                    className={`${Styles['action-button']} ${Styles.reject}`}
                                />
                            )
                        }
                    </div>
                ),
            ],
        };
    });

    return (
        <>
            <Table
                header={tableHeader}
                body={tableBody}
                isLoading={getPendingInventoryTransactionListingIsLoading}
            />

            <ViewSerialNoModalModal
                isOpen={isSerialNoModalOpen}
                onClose={() => setIsSerialNoModalOpen(false)}
                serialNo={serialNoList}
            />
        </>
    );
};

export default GoldInventoryApprovalTable;
